import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PinnedItemType, usePinnedItems } from "services/pinnedItems";
import { Role } from "services/rbac/types";
import { useRbacRoles } from "services/rbac/useRbacRoles";

import ShieldIcon from "assets/icons/roleIcons/Shield.svg?react";

import { useMenu } from "components/ActionMenu/useMenu";
import Highlighter from "components/Highligher/Highlighter";
import { PinIconEmpty, PinIconFill } from "components/Icons";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";
import { useRoleMenuItems } from "components/LeftSidebar/SecurityPanel/RoleRecord";
import DirectorySearchPanel from "components/LeftSidebarV2/DirectorySearchPanel/DirectorySearchPanel";

import styles from "./RolesDirectory.module.scss";

const RoleItem = (props: {
  role: Role;
  isPinned: boolean;
  onTogglePin: () => void;
  searchQuery: string;
}) => {
  const { role, isPinned, onTogglePin, searchQuery } = props;
  const menu = useMenu();

  const { items, modalsMarkup } = useRoleMenuItems({
    role,
    menu,
  });
  return (
    <>
      <div className={styles.userItem}>
        <div className={styles.left}>
          <div className={styles.icon}>
            <ShieldIcon />
          </div>
          <div className={styles.title}>
            {searchQuery ? (
              <Highlighter
                searchWords={[searchQuery]}
                textToHighlight={role.roleName}
              />
            ) : (
              role.roleName
            )}
          </div>
        </div>
        <div className={styles.right}>
          <div
            className={styles.pinButton}
            onClick={onTogglePin}
          >
            {isPinned ? <PinIconFill /> : <PinIconEmpty />}
          </div>
          <Menu
            testId="user-more-icon"
            items={items}
            menu={menu}
          />
        </div>
      </div>
      {modalsMarkup}
    </>
  );
};

const RolesDirectory = () => {
  const { t } = useTranslation();
  const roles = useRbacRoles();
  const { pinnedItems, togglePinned } = usePinnedItems(PinnedItemType.ROLE);
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [search]);

  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.roleName.toLowerCase().includes(search.toLowerCase())
    );
  }, [roles, search]);

  const sortedRoles = useMemo(() => {
    const pinnedSet = new Set(pinnedItems);
    const pinned = filteredRoles.filter(role => pinnedSet.has(role.roleName));
    const unpinned = filteredRoles.filter(
      role => !pinnedSet.has(role.roleName)
    );

    return { pinned, unpinned };
  }, [filteredRoles, pinnedItems]);

  return (
    <div className={styles.usersDirectory}>
      <div className={styles.searchPanel}>
        <DirectorySearchPanel
          onSearch={setSearch}
          search={search}
          testId="roles-search-input"
        />
      </div>

      <div
        className={styles.userList}
        ref={listRef}
      >
        {sortedRoles.pinned.length > 0 && (
          <div className={styles.pinnedUsersHeader}>
            {t("workspace.directory.pinned")}
          </div>
        )}
        {sortedRoles.pinned.map(role => (
          <RoleItem
            role={role}
            key={role.roleName}
            isPinned={true}
            onTogglePin={() => togglePinned(role.roleName)}
            searchQuery={search}
          />
        ))}
        {sortedRoles.pinned.length > 0 && sortedRoles.unpinned.length > 0 && (
          <div className={styles.separator} />
        )}
        {sortedRoles.unpinned.map(role => (
          <RoleItem
            role={role}
            key={role.roleName}
            isPinned={false}
            onTogglePin={() => togglePinned(role.roleName)}
            searchQuery={search}
          />
        ))}
      </div>
    </div>
  );
};

export default RolesDirectory;
